import React, { useState, useEffect, useCallback, useRef } from 'react';
// D16-B — canonical supervision media-leg construction (single Manager↔Operator auxiliary leg; the
// Customer is never a supervisor media peer). Live WS offer/answer/ICE wiring + the media-connected
// 'confirm' round-trip complete at the runtime milestone; this screen constructs/closes the aux leg only.
import { createOperatorSupervisionLeg, leaveSupervision, startWhisper } from '../../../lib/supervisionMedia';
import { createSupervisorTransport, createSupervisorHandshake } from '../../../lib/supervisionSignaling';
import { getCurrentIdToken } from '../../../lib/firebase';
import { getOrCreateInstallationId } from '../../../lib/installationIdentity';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../hooks/use-toast';
import { getErrorMessage } from '../../../lib/errorMessage';
import {
  Users, Clock, Phone, MessageSquare, TrendingUp, AlertCircle, Headphones,
  Star, Calendar, BarChart3, Zap, Mic, Settings, RefreshCw, Trash2, Plus, X
} from 'lucide-react';

// Manage Operators — Manager/Admin control surface. Every control is wired to a REAL
// tenant-scoped endpoint (no fabricated data, no fake success). Writes are Manager/Admin
// only and enforced server-side; the UI reflects honest empty / unavailable states.
const ManageOperators = () => {
  const { getBusinessStaff, currentBusiness } = useBusiness();
  const { makeAuthenticatedRequest, user } = useCRMAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [monitoringMode, setMonitoringMode] = useState('dashboard');

  const bid = currentBusiness?.brandId;
  const businessStaff = getBusinessStaff();
  const operators = businessStaff.filter(staff => staff.role === 'operator');
  const operatorIdsKey = operators.map(o => o.id).filter(Boolean).join(',');

  // Mode → data period + live behaviour. The selector genuinely changes the data source,
  // not just a label: dashboard/realtime = today; realtime also auto-refreshes; historical = 30d.
  const periodForMode = (m) => (m === 'historical' ? '30d' : 'today');

  // ── Operator KPIs (real) ──────────────────────────────────────────────────
  const [kpiMap, setKpiMap] = useState({});
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState(null);

  const loadKpis = useCallback(async () => {
    const ids = operatorIdsKey ? operatorIdsKey.split(',') : [];
    if (!bid || ids.length === 0) { setKpiMap({}); return; }
    setKpiLoading(true); setKpiError(null);
    const period = periodForMode(monitoringMode);
    try {
      const next = {};
      const CONCURRENCY = 4;
      for (let i = 0; i < ids.length; i += CONCURRENCY) {
        const slice = ids.slice(i, i + CONCURRENCY);
        const settled = await Promise.allSettled(slice.map(async (id) => {
          const r = await makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(id)}/kpis?business_id=${encodeURIComponent(bid)}&period=${period}`);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return [id, await r.json()];
        }));
        settled.forEach((s) => { if (s.status === 'fulfilled') { next[s.value[0]] = s.value[1]; } });
      }
      setKpiMap(next);
      if (Object.keys(next).length === 0) setKpiError('Could not load operator metrics. Try refresh.');
    } catch (e) {
      setKpiError(e.message || 'Failed to load operator metrics.');
    } finally {
      setKpiLoading(false);
    }
  }, [bid, operatorIdsKey, monitoringMode]);

  useEffect(() => { loadKpis(); }, [loadKpis]);

  // realtime mode → poll every 15s
  useEffect(() => {
    if (monitoringMode !== 'realtime') return undefined;
    const h = setInterval(() => { loadKpis(); }, 15000);
    return () => clearInterval(h);
  }, [monitoringMode, loadKpis]);

  const EMPTY_KPI = { calls_today: null, tickets_resolved: null, active_tickets: null, aht_minutes: null, fcr_percent: null, csat: null, callback_compliance: null };
  const kpiFor = (id) => kpiMap[id] || EMPTY_KPI;
  const fmtNum = (v, suffix = '') => (v === null || v === undefined) ? '—' : `${v}${suffix}`;
  const fmtAht = (m) => (m === null || m === undefined) ? '—' : `${Math.floor(m)}:${String(Math.round((m % 1) * 60)).padStart(2, '0')}`;
  const aggSum = (field) => {
    const vals = operators.map(o => kpiMap[o.id]?.[field]).filter(v => v !== null && v !== undefined);
    return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
  };
  const aggAvg = (field) => {
    const vals = operators.map(o => kpiMap[o.id]?.[field]).filter(v => v !== null && v !== undefined);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  };
  const getStatusColor = (status) => {
    switch (status) {
      case 'on_call': return 'bg-red-100 text-red-800';
      case 'available': return 'bg-green-100 text-green-800';
      case 'break': return 'bg-yellow-100 text-yellow-800';
      case 'offline': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };
  const apiErr = async (r, fallback) => {
    // Always return a STRING (never a raw {code,message}/array) — this feeds both a banner notice and a
    // toast title, and an object child would throw React #31. Covers the supervision 503 governed notice.
    try { const j = await r.json(); return getErrorMessage({ data: j, status: r.status }, fallback); } catch { return fallback; }
  };

  // ── Per-operator: activity / skills / coaching / call-state (real) ─────────
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [skills, setSkills] = useState({ skills: [], languages: [], certifications: [] });
  const [skillDraft, setSkillDraft] = useState({ skills: '', languages: '', certifications: '' });
  const [coaching, setCoaching] = useState([]);
  const [callState, setCallState] = useState({ active: false, reason: '' });
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachForm, setCoachForm] = useState({ type: 'coaching', purpose: '', notes: '', scheduled_date: '' });

  const loadOperatorDetail = useCallback(async (op) => {
    if (!op || !bid) return;
    const q = `business_id=${encodeURIComponent(bid)}`;
    setActivityLoading(true);
    try {
      const [aR, sR, cR, csR] = await Promise.all([
        makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(op.id)}/activity?${q}`),
        makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(op.id)}/skills?${q}`),
        makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(op.id)}/coaching?${q}`),
        makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(op.id)}/call-state?${q}`),
      ]);
      setActivity(aR.ok ? await aR.json() : null);
      const s = sR.ok ? await sR.json() : { skills: [], languages: [], certifications: [] };
      setSkills({ skills: s.skills || [], languages: s.languages || [], certifications: s.certifications || [] });
      setCoaching(cR.ok ? (await cR.json()).items || [] : []);
      setCallState(csR.ok ? await csR.json() : { active: false, reason: 'No active operator call' });
    } finally {
      setActivityLoading(false);
    }
  }, [bid, makeAuthenticatedRequest]);

  useEffect(() => { if (selectedOperator) loadOperatorDetail(selectedOperator); }, [selectedOperator, loadOperatorDetail]);

  const saveSkills = async () => {
    if (!selectedOperator || !bid) return;
    const merge = (existing, draft) => {
      const add = draft.split(',').map(x => x.trim()).filter(Boolean);
      return Array.from(new Set([...existing, ...add]));
    };
    const body = {
      skills: merge(skills.skills, skillDraft.skills),
      languages: merge(skills.languages, skillDraft.languages),
      certifications: merge(skills.certifications, skillDraft.certifications),
    };
    const r = await makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(selectedOperator.id)}/skills?business_id=${encodeURIComponent(bid)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (r.ok) {
      const s = await r.json();
      setSkills({ skills: s.skills || [], languages: s.languages || [], certifications: s.certifications || [] });
      setSkillDraft({ skills: '', languages: '', certifications: '' });
      toast({ title: t('ops.ops.skillsSaved') || 'Skills saved' });
    } else {
      toast({ title: await apiErr(r, 'Could not save skills'), variant: 'destructive' });
    }
  };

  const removeSkill = async (field, value) => {
    if (!selectedOperator || !bid) return;
    const body = { [field]: skills[field].filter(v => v !== value) };
    const r = await makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(selectedOperator.id)}/skills?business_id=${encodeURIComponent(bid)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (r.ok) { const s = await r.json(); setSkills({ skills: s.skills || [], languages: s.languages || [], certifications: s.certifications || [] }); }
  };

  const submitCoaching = async () => {
    if (!selectedOperator || !bid) return;
    const r = await makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(selectedOperator.id)}/coaching?business_id=${encodeURIComponent(bid)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(coachForm),
    });
    if (r.ok) {
      setCoachOpen(false);
      setCoachForm({ type: 'coaching', purpose: '', notes: '', scheduled_date: '' });
      await loadOperatorDetail(selectedOperator);
      toast({ title: t('ops.ops.coachingSaved') || 'Scheduled' });
    } else {
      toast({ title: await apiErr(r, 'Could not schedule'), variant: 'destructive' });
    }
  };

  // Supervision (Whisper = coach; Join = full participant) — DISTINCT actions; live auxiliary media leg
  // over a DEDICATED authenticated Supervisor socket (the canonical protocol authority). The primary
  // Customer↔Operator call is never touched; the customer is never a supervision target.
  const [superviseState, setSuperviseState] = useState({ mode: null, state: 'idle', notice: '' });
  const superLegRef = useRef(null);        // auxiliary Manager↔Operator PC (never the primary call)
  const superTransportRef = useRef(null);  // dedicated authenticated Supervisor socket lifecycle owner
  const superHandshakeRef = useRef(null);

  const superBackendUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.servisfy.com';
  const superWsUrl = superBackendUrl.replace('http://', 'ws://').replace('https://', 'wss://');

  const teardownSupervision = () => {
    try { const el = superLegRef.current && superLegRef.current._mgrAudioEl; if (el) { el.srcObject = null; superLegRef.current._mgrAudioEl = null; } } catch (_) { /* noop */ }
    try { if (superTransportRef.current) superTransportRef.current.close(); } catch (_) { /* noop */ }
    leaveSupervision(superLegRef.current);
    superTransportRef.current = null; superHandshakeRef.current = null; superLegRef.current = null;
  };

  const supervise = async (mode) => {
    if (!callState.active) { toast({ title: t('ops.ops.noActiveCall') || 'Operator has no active call' }); return; }
    setSuperviseState({ mode, state: 'connecting', notice: '' });
    const r = await makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(selectedOperator.id)}/supervise?business_id=${encodeURIComponent(bid)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode, action: 'start' }),
    });
    if (!r.ok) {
      const detail = await apiErr(r, 'Supervision failed');
      setSuperviseState({ mode: null, state: 'failed', notice: detail });
      toast({ title: detail, variant: 'destructive' });
      return;
    }
    const j = await r.json();
    // Backend has only AUTHORIZED + started SIGNALING (never media-connected). Drive the live auxiliary leg;
    // ONLY a connected PeerConnection + backend 'confirm' flips the UI to listening/whisper-active.
    setSuperviseState({ mode, state: 'signaling', notice: j.notice || '' });
    const opLeg = Array.isArray(j.legs) ? j.legs.find((l) => l.target_role === 'operator') : null;
    if (!opLeg || typeof window === 'undefined' || typeof window.RTCPeerConnection !== 'function') return; // SSR/test no-op
    try {
      // P0-D media-plane fix: the Manager supervisor leg MUST use the SAME governed ICE/TURN relay as the primary
      // call (the operator answer side already does). With WEBRTC_REQUIRE_TURN=true, an empty iceServers set leaves
      // the supervisor PeerConnection with only host candidates, so Whisper/Join return API 200 but the Manager
      // media leg never connects across NAT. Fetch the canonical ICE servers and build the leg with them.
      let supervisorIce = [];
      try {
        const iceRes = await makeAuthenticatedRequest('/api/v1/webrtc/ice-servers', { method: 'GET' });
        if (iceRes.ok) { const cfg = await iceRes.json(); supervisorIce = Array.isArray(cfg.iceServers) ? cfg.iceServers : []; }
      } catch (_) { /* leave empty — media fails visibly rather than silently */ }
      const leg = createOperatorSupervisionLeg(opLeg, { createPeerConnection: (cfg) => new window.RTCPeerConnection(cfg), iceServers: supervisorIce });
      superLegRef.current = leg;
      // P0-D M-1: render the remote supervision audio on the MANAGER side (the Manager must HEAR the live
      // call). A private <audio> sink — never attached to the customer. Whisper privacy stays structural:
      // the customer has no supervisor leg, so no Manager audio can ever reach the customer through this.
      leg.pc.ontrack = (e) => {
        try { const el = new window.Audio(); el.autoplay = true; el.srcObject = e.streams[0]; el.play().catch(() => {}); leg._mgrAudioEl = el; }
        catch (_) { /* noop — playback best-effort */ }
      };
      // Whisper acquires the microphone at START only, adding it to the auxiliary leg ONLY (never the customer).
      if (mode === 'whisper' && navigator.mediaDevices?.getUserMedia) {
        try { const mic = await navigator.mediaDevices.getUserMedia({ audio: true }); startWhisper(leg, mic.getAudioTracks()[0], mic); }
        catch (_) { setSuperviseState({ mode, state: 'failed', notice: 'Microphone permission is required to whisper.' }); teardownSupervision(); return; }
      }
      const transport = createSupervisorTransport({
        wsUrl: superWsUrl, businessId: bid, managerId: user?.uid || j.manager_user_id,
        getToken: getCurrentIdToken, installationId: getOrCreateInstallationId(),
      });
      superTransportRef.current = transport;
      // P0-D observability (diagnostic-only): a safe supervisor-diag beacon over the EXISTING supervisor
      // socket. Names/counts/states only — never SDP/ICE bodies/tokens. Never alters media/signaling.
      const _turnCount = supervisorIce.filter((s) => {
        const arr = Array.isArray(s && s.urls) ? s.urls : [s && s.urls];
        return arr.some((x) => typeof x === 'string' && x.toLowerCase().startsWith('turn'));
      }).length;
      const diag = (event, extra = {}) => {
        try { transport.send({ type: 'supervisor-diag', event, role: 'manager', mode,
          call_id: j.session_id, supervision_session_id: j.supervision_session_id, ...extra }); }
        catch (_) { /* diagnostics never break supervision */ }
      };
      // SUPERVISION_CLIENT_CONTRACT_V1: the Android/iOS operator receivers REQUIRE the full id set on
      // every supervisor frame — the offer is DROPPED without conn_id, and the operator routes its answer
      // back via manager_user_id. Echo the backend leg descriptor + session ids. audio_direction is the
      // OPERATOR's direction (sendrecv in BOTH modes → operator sends mic so the Manager hears it).
      const frameMeta = {
        conn_id: opLeg.conn_id,
        target_user_id: opLeg.target_user_id,
        target_role: opLeg.target_role || 'operator',
        manager_user_id: j.manager_user_id || user?.uid,
        primary_call_session_id: j.session_id,
        mode,
        audio_direction: opLeg.target_direction || 'sendrecv',
        tenant_id: j.tenant_id || bid,
      };
      const hs = createSupervisorHandshake({
        transport, leg, callId: j.session_id, supervisionSessionId: j.supervision_session_id,
        operatorTarget: opLeg.target_user_id, frameMeta,
        diag, diagMeta: { mode, ice_server_count: supervisorIce.length, turn_server_count: _turnCount },
        confirm: () => makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(selectedOperator.id)}/supervise?business_id=${encodeURIComponent(bid)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode, action: 'confirm' }) }),
        onListening: () => setSuperviseState({ mode, state: mode === 'whisper' ? 'whisper_active' : 'listening', notice: '' }),
        onError: () => setSuperviseState({ mode, state: 'signaling', notice: 'Media confirmation pending.' }),
      });
      superHandshakeRef.current = hs;
      transport.open((msg) => { hs.onSignal(msg).catch(() => {}); });
      await hs.start();
    } catch (_) { teardownSupervision(); setSuperviseState({ mode: null, state: 'failed', notice: 'Supervision could not connect.' }); }
  };

  const stopSupervise = async () => {
    const mode = superviseState.mode;
    const r = await makeAuthenticatedRequest(`/api/v1/analytics/operator/${encodeURIComponent(selectedOperator.id)}/supervise?business_id=${encodeURIComponent(bid)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode, action: 'stop' }),
    });
    // Leaving closes ONLY the auxiliary supervision leg + supervisor socket; the primary call is untouched.
    teardownSupervision();
    setSuperviseState({ mode: null, state: r.ok ? 'disconnected' : 'idle', notice: '' });
  };

  // reset supervision when switching operators (and close any auxiliary leg — never the primary call)
  useEffect(() => {
    teardownSupervision();
    setSuperviseState({ mode: null, state: 'idle', notice: '' });
  }, [selectedOperator?.id]);

  // ── Shifts dialog (real) ──────────────────────────────────────────────────
  const [shiftsOpen, setShiftsOpen] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [shiftForm, setShiftForm] = useState({ operator_id: '', date: '', start_time: '', end_time: '', shift_type: 'regular' });
  const loadShifts = useCallback(async () => {
    if (!bid) return;
    const r = await makeAuthenticatedRequest(`/api/v1/analytics/business/${encodeURIComponent(bid)}/shifts?business_id=${encodeURIComponent(bid)}`);
    if (r.ok) setShifts((await r.json()).shifts || []);
  }, [bid, makeAuthenticatedRequest]);
  useEffect(() => { if (shiftsOpen) loadShifts(); }, [shiftsOpen, loadShifts]);
  const createShift = async () => {
    const r = await makeAuthenticatedRequest(`/api/v1/analytics/business/${encodeURIComponent(bid)}/shifts?business_id=${encodeURIComponent(bid)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(shiftForm),
    });
    if (r.ok) { setShiftForm({ operator_id: '', date: '', start_time: '', end_time: '', shift_type: 'regular' }); await loadShifts(); toast({ title: t('ops.ops.shiftCreated') || 'Shift created' }); }
    else toast({ title: await apiErr(r, 'Could not create shift'), variant: 'destructive' });
  };
  const deleteShift = async (id) => {
    const r = await makeAuthenticatedRequest(`/api/v1/analytics/business/${encodeURIComponent(bid)}/shifts/${encodeURIComponent(id)}?business_id=${encodeURIComponent(bid)}`, { method: 'DELETE' });
    if (r.ok) await loadShifts();
  };
  const operatorName = (id) => operators.find(o => o.id === id)?.name || id;

  // ── Queue config dialog (real) ────────────────────────────────────────────
  const [queueOpen, setQueueOpen] = useState(false);
  const [queueCfg, setQueueCfg] = useState({ max_wait_time: 120, priority_routing: true, skill_based_routing: true, auto_assignment: true, max_concurrent_calls: 3, escalation_time: 300 });
  const [queueSaving, setQueueSaving] = useState(false);
  const loadQueueCfg = useCallback(async () => {
    if (!bid) return;
    const r = await makeAuthenticatedRequest(`/api/v1/analytics/business/${encodeURIComponent(bid)}/queue-config?business_id=${encodeURIComponent(bid)}`);
    if (r.ok) { const c = await r.json(); setQueueCfg({ max_wait_time: c.max_wait_time, priority_routing: c.priority_routing, skill_based_routing: c.skill_based_routing, auto_assignment: c.auto_assignment, max_concurrent_calls: c.max_concurrent_calls, escalation_time: c.escalation_time }); }
  }, [bid, makeAuthenticatedRequest]);
  useEffect(() => { if (queueOpen) loadQueueCfg(); }, [queueOpen, loadQueueCfg]);
  const saveQueueCfg = async () => {
    setQueueSaving(true);
    try {
      const r = await makeAuthenticatedRequest(`/api/v1/analytics/business/${encodeURIComponent(bid)}/queue-config?business_id=${encodeURIComponent(bid)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(queueCfg),
      });
      if (r.ok) { await loadQueueCfg(); toast({ title: t('ops.ops.queueSaved') || 'Queue settings saved' }); }
      else toast({ title: await apiErr(r, 'Could not save queue settings'), variant: 'destructive' });
    } finally { setQueueSaving(false); }
  };

  const chip = (field, list) => (
    <div className="flex flex-wrap gap-2">
      {list.length === 0 ? <span className="text-sm text-gray-400">—</span> : list.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
          {v}
          <button onClick={() => removeSkill(field, v)} className="hover:text-red-600"><X className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('ops.ops.manageOperators')}</h1>
            <p className="text-gray-600">{t('ops.ops.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <Select value={monitoringMode} onValueChange={setMonitoringMode}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dashboard">{t('ops.ops.dashboard')}</SelectItem>
                <SelectItem value="realtime">{t('ops.ops.realtime')}</SelectItem>
                <SelectItem value="historical">{t('ops.ops.historical')}</SelectItem>
              </SelectContent>
            </Select>

            {/* Schedule Shifts — real dialog */}
            <Dialog open={shiftsOpen} onOpenChange={setShiftsOpen}>
              <DialogTrigger asChild>
                <Button variant="outline"><Calendar className="h-4 w-4 mr-2" />{t('ops.ops.scheduleShifts')}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>{t('ops.ops.scheduleShifts')}</DialogTitle><DialogDescription>{t('ops.ops.scheduleShiftsDesc') || 'Create, list and remove operator work shifts.'}</DialogDescription></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <Select value={shiftForm.operator_id} onValueChange={(v) => setShiftForm(s => ({ ...s, operator_id: v }))}>
                      <SelectTrigger><SelectValue placeholder={t('ops.ops.operator') || 'Operator'} /></SelectTrigger>
                      <SelectContent>{operators.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="date" value={shiftForm.date} onChange={e => setShiftForm(s => ({ ...s, date: e.target.value }))} />
                    <Input type="time" value={shiftForm.start_time} onChange={e => setShiftForm(s => ({ ...s, start_time: e.target.value }))} />
                    <Input type="time" value={shiftForm.end_time} onChange={e => setShiftForm(s => ({ ...s, end_time: e.target.value }))} />
                    <Button onClick={createShift} disabled={!shiftForm.operator_id || !shiftForm.date}><Plus className="h-4 w-4 mr-1" />{t('common.add') || 'Add'}</Button>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y">
                    {shifts.length === 0 ? <p className="text-sm text-gray-400 py-4 text-center">{t('ops.ops.noShifts') || 'No shifts scheduled'}</p> :
                      shifts.map(s => (
                        <div key={s.id} className="flex items-center justify-between py-2 text-sm">
                          <span>{operatorName(s.operator_id)} · {s.date} · {s.start_time || '—'}–{s.end_time || '—'} · {s.shift_type}</span>
                          <button onClick={() => deleteShift(s.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Queue Settings — real dialog */}
            <Dialog open={queueOpen} onOpenChange={setQueueOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-purple-600"><Settings className="h-4 w-4 mr-2" />{t('ops.ops.queueSettings')}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('ops.ops.queueSettings')}</DialogTitle><DialogDescription>{t('ops.ops.queueSettingsDesc') || 'Configure how calls and tickets are routed and escalated for this tenant.'}</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  {[['max_wait_time', t('ops.ops.maxWaitTime') || 'Max wait time (s)'], ['max_concurrent_calls', t('ops.ops.maxConcurrent') || 'Max concurrent calls'], ['escalation_time', t('ops.ops.escalationTime') || 'Escalation time (s)']].map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between gap-3">
                      <span className="text-sm">{label}</span>
                      <Input type="number" className="w-28" value={queueCfg[k]} onChange={e => setQueueCfg(c => ({ ...c, [k]: e.target.value }))} />
                    </div>
                  ))}
                  {[['priority_routing', t('ops.ops.priorityRouting') || 'Priority routing'], ['skill_based_routing', t('ops.ops.skillRouting') || 'Skill-based routing'], ['auto_assignment', t('ops.ops.autoAssign') || 'Auto assignment']].map(([k, label]) => (
                    <label key={k} className="flex items-center justify-between gap-3 text-sm cursor-pointer">
                      <span>{label}</span>
                      <input type="checkbox" checked={!!queueCfg[k]} onChange={e => setQueueCfg(c => ({ ...c, [k]: e.target.checked }))} />
                    </label>
                  ))}
                  <Button onClick={saveQueueCfg} disabled={queueSaving} className="w-full">{queueSaving ? '…' : (t('common.save') || 'Save')}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Real-time Overview */}
        <div className="grid md:grid-cols-5 gap-6 mb-8">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-gray-600">{t('ops.ops.onlineOperators')}</p><p className="text-2xl font-bold text-green-600">{operators.length}</p></div><Users className="h-8 w-8 text-green-600" /></div></CardContent></Card>
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-gray-600">{t('ops.ops.callsToday')}</p><p className="text-2xl font-bold text-red-600">{fmtNum(aggSum('calls_today'))}</p></div><Phone className="h-8 w-8 text-red-600" /></div></CardContent></Card>
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-gray-600">{t('ops.ops.ticketsResolved')}</p><p className="text-2xl font-bold text-violet-600">{fmtNum(aggSum('tickets_resolved'))}</p></div><MessageSquare className="h-8 w-8 text-violet-600" /></div></CardContent></Card>
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-gray-600">{t('ops.ops.activeTickets')}</p><p className="text-2xl font-bold text-orange-600">{fmtNum(aggSum('active_tickets'))}</p></div><Clock className="h-8 w-8 text-orange-600" /></div></CardContent></Card>
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-gray-600">{t('ops.ops.avgAht')}</p><p className="text-2xl font-bold text-blue-600">{fmtAht(aggAvg('aht_minutes'))}</p></div><TrendingUp className="h-8 w-8 text-blue-600" /></div></CardContent></Card>
        </div>

        {monitoringMode === 'realtime' && (
          <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <span className="text-sm text-amber-800 font-medium">{t('ops.ops.realtimeModeActive')}</span>
          </div>
        )}
        {monitoringMode === 'historical' && (
          <div className="mb-6 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">{t('ops.ops.historicalMode') || 'Showing the last 30 days.'}</div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Operators list */}
        <div className="lg:col-span-1">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('ops.ops.activeOperators')}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{operators.length}</Badge>
                  <Button variant="ghost" size="sm" onClick={loadKpis} disabled={kpiLoading} title={t('fin.mo.refreshMetrics')}>
                    <RefreshCw className={`h-4 w-4 ${kpiLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {kpiError && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{kpiError}</div>}
              {operators.length === 0 ? (
                <div className="text-center py-8 text-gray-500"><Users className="h-10 w-10 mx-auto mb-2 text-gray-400" /><p>{t('ops.ops.noOperators')}</p></div>
              ) : (
                <div className="space-y-4">
                  {operators.map((operator) => {
                    const kpi = kpiFor(operator.id);
                    return (
                      <div key={operator.id} className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${selectedOperator?.id === operator.id ? 'bg-gradient-to-r from-blue-100 to-purple-100 border-2 border-purple-300' : 'bg-gray-50 hover:bg-gray-100'}`} onClick={() => setSelectedOperator(operator)}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <Avatar className="h-10 w-10"><AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-500 text-white font-semibold">{operator.name.split(' ').map(n => n[0]).join('')}</AvatarFallback></Avatar>
                            <div><h4 className="font-semibold text-gray-900">{operator.name}</h4><p className="text-sm text-gray-600">{operator.email}</p></div>
                          </div>
                          <Badge className={getStatusColor(operator.status || 'unknown')}>{operator.status || 'unknown'}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-center p-2 bg-white rounded"><div className="font-semibold text-blue-700">{fmtNum(kpi.calls_today)}</div><div className="text-blue-600">{t('ops.ops.calls')}</div></div>
                          <div className="text-center p-2 bg-white rounded"><div className="font-semibold text-green-700">{fmtNum(kpi.fcr_percent, '%')}</div><div className="text-green-600">{t('ops.ops.fcr')}</div></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {selectedOperator ? (
            <div className="space-y-6">
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center space-x-4">
                      <Avatar className="h-16 w-16"><AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-500 text-white text-xl font-bold">{selectedOperator.name.split(' ').map(n => n[0]).join('')}</AvatarFallback></Avatar>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{selectedOperator.name}</h2>
                        <div className="flex items-center space-x-3 mt-2">
                          <Badge className={getStatusColor(activity?.presence || selectedOperator.status || 'unknown')}>{activity?.presence || selectedOperator.status || 'unknown'}</Badge>
                          <Badge variant="outline">{selectedOperator.role || 'operator'}</Badge>
                          <div className="text-sm text-gray-600">{selectedOperator.email}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {['signaling', 'listening', 'whisper_active'].includes(superviseState.state) ? (
                        <div className="flex flex-col items-end gap-1">
                          {/* D16-B — state reflects the ACTUAL auxiliary PeerConnection: 'signaling' until it
                              connects + backend confirm, then 'listening' (join) / 'whisper_active'. Physical
                              audio (operator hears whisper; customer hears nothing) is verified at release. */}
                          <div className={`text-sm font-medium px-3 py-1 rounded ${superviseState.state === 'whisper_active' ? 'bg-purple-100 text-purple-800' : superviseState.state === 'listening' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>
                            {superviseState.state === 'whisper_active'
                              ? (t('ops.ops.whisperActiveNotice') || 'Whisper active — coaching the operator (audio verified at release).')
                              : superviseState.state === 'listening'
                              ? (t('ops.ops.listeningNotice') || 'Listening to the call (audio verified at release).')
                              : (t('ops.ops.supervisionConnectingNotice') || 'Connecting supervision…')}
                          </div>
                          <Button variant="destructive" size="sm" onClick={stopSupervise}>
                            <X className="h-4 w-4 mr-2" />{t('ops.ops.stopSupervision') || 'Stop supervision'}
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex space-x-2">
                            <Button variant="outline" size="sm"
                              disabled={!callState.active || superviseState.state === 'connecting'}
                              onClick={() => supervise('whisper')}
                              title={callState.active ? (t('ops.ops.whisperHint') || 'Whisper privately to the operator') : (t('ops.ops.noActiveCall') || 'Operator has no active call')}>
                              <Mic className="h-4 w-4 mr-2" />{t('ops.ops.whisper')}
                              {superviseState.state === 'connecting' && superviseState.mode === 'whisper' && '…'}
                            </Button>
                            <Button variant="outline" size="sm"
                              disabled={!callState.active || superviseState.state === 'connecting'}
                              onClick={() => supervise('join')}
                              title={callState.active ? (t('ops.ops.joinHint') || 'Join the call as a full participant') : (t('ops.ops.noActiveCall') || 'Operator has no active call')}>
                              <Headphones className="h-4 w-4 mr-2" />{t('ops.ops.joinCall')}
                              {superviseState.state === 'connecting' && superviseState.mode === 'join' && '…'}
                            </Button>
                          </div>
                          {!callState.active && <span className="text-xs text-gray-500">{t('ops.ops.noActiveCall') || 'Operator has no active call'}</span>}
                          {superviseState.state === 'failed' && <span className="text-xs text-red-600">{superviseState.notice}</span>}
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>

              <Tabs defaultValue="performance" className="space-y-6">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="performance">{t('ops.ops.performance')}</TabsTrigger>
                  <TabsTrigger value="activity">{t('ops.ops.liveActivity')}</TabsTrigger>
                  <TabsTrigger value="skills">{t('ops.ops.skills')}</TabsTrigger>
                  <TabsTrigger value="coaching">{t('ops.ops.coaching')}</TabsTrigger>
                </TabsList>

                <TabsContent value="performance" className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                      <CardHeader><CardTitle className="text-lg">{t('ops.ops.kpis')}</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex justify-between items-center"><span className="text-sm font-medium">{t('ops.ops.avgHandleTime')}</span><span className="font-bold text-blue-600">{fmtAht(kpiFor(selectedOperator.id).aht_minutes)}</span></div>
                        <div className="flex justify-between items-center"><span className="text-sm font-medium">{t('ops.ops.firstCallResolution')}</span><span className="font-bold text-green-600">{fmtNum(kpiFor(selectedOperator.id).fcr_percent, '%')}</span></div>
                        <div className="flex justify-between items-center"><span className="text-sm font-medium">{t('ops.ops.customerSatisfaction')}</span><div className="flex items-center"><Star className="h-4 w-4 text-yellow-500 fill-current mr-1" /><span className="font-bold">{fmtNum(kpiFor(selectedOperator.id).csat)}</span></div></div>
                        <div className="flex justify-between items-center"><span className="text-sm font-medium">{t('ops.ops.callbackCompliance')}</span><span className="font-bold text-purple-600">{fmtNum(kpiFor(selectedOperator.id).callback_compliance, '%')}</span></div>
                        <div className="flex justify-between items-center"><span className="text-sm font-medium">{t('ops.ops.activeTickets')}</span><span className="font-bold text-orange-600">{fmtNum(kpiFor(selectedOperator.id).active_tickets)}</span></div>
                      </CardContent>
                    </Card>
                    <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                      <CardHeader><CardTitle className="text-lg">{t('ops.ops.todaysStats')}</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center p-3 bg-blue-50 rounded-lg"><div className="text-2xl font-bold text-blue-700">{fmtNum(kpiFor(selectedOperator.id).calls_today)}</div><div className="text-sm text-blue-600">{t('ops.ops.callsHandled')}</div></div>
                          <div className="text-center p-3 bg-green-50 rounded-lg"><div className="text-2xl font-bold text-green-700">{fmtNum(kpiFor(selectedOperator.id).tickets_resolved)}</div><div className="text-sm text-green-600">{t('ops.ops.ticketsResolved')}</div></div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Live Activity — real */}
                <TabsContent value="activity" className="space-y-6">
                  <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                    <CardHeader><CardTitle className="text-lg flex items-center justify-between"><span>{t('ops.ops.liveActivity')}</span><Button variant="ghost" size="sm" onClick={() => loadOperatorDetail(selectedOperator)} disabled={activityLoading}><RefreshCw className={`h-4 w-4 ${activityLoading ? 'animate-spin' : ''}`} /></Button></CardTitle></CardHeader>
                    <CardContent>
                      {!activity ? <div className="text-center py-8 text-gray-500">{t('ops.ops.noActivity') || 'No current activity'}</div> : (
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between"><span className="text-gray-600">{t('ops.ops.presence') || 'Presence'}</span><Badge className={getStatusColor(activity.presence)}>{activity.presence}</Badge></div>
                          <div className="flex justify-between"><span className="text-gray-600">{t('ops.ops.onCall') || 'On call'}</span><span className="font-medium">{activity.on_call ? (activity.call?.display || activity.call?.customer_name || 'On a call') : (t('ops.ops.noActiveCall') || 'No active operator call')}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">{t('ops.ops.activeTickets')}</span><span className="font-medium">{activity.active_tickets ?? 0}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">{t('ops.ops.currentConversation') || 'Current conversation'}</span><span className="font-medium">{activity.current_conversation ? (activity.current_conversation.subject || activity.current_conversation.id || 'active') : '—'}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">{t('ops.ops.lastActivity') || 'Last activity'}</span><span className="font-medium">{activity.last_activity ? new Date(activity.last_activity).toLocaleString() : '—'}</span></div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Skills — real, editable (Manager/Admin; server-enforced) */}
                <TabsContent value="skills" className="space-y-6">
                  <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                    <CardHeader><CardTitle className="text-lg">{t('ops.ops.skillsCerts')}</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                      {[['skills', t('ops.ops.skills')], ['languages', t('ops.ops.languages') || 'Languages'], ['certifications', t('ops.ops.certifications') || 'Certifications']].map(([field, label]) => (
                        <div key={field} className="space-y-2">
                          <div className="text-sm font-medium">{label}</div>
                          {chip(field, skills[field])}
                          <div className="flex gap-2">
                            <Input placeholder={`${t('common.add') || 'Add'} ${label} (comma-separated)`} value={skillDraft[field]} onChange={e => setSkillDraft(d => ({ ...d, [field]: e.target.value }))} />
                          </div>
                        </div>
                      ))}
                      <Button onClick={saveSkills} disabled={!skillDraft.skills && !skillDraft.languages && !skillDraft.certifications}><Plus className="h-4 w-4 mr-1" />{t('common.save') || 'Save'}</Button>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Coaching — real */}
                <TabsContent value="coaching" className="space-y-6">
                  <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                    <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Zap className="h-5 w-5 text-yellow-500" />{t('ops.ops.coachingDev')}</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <Button className="flex-1" onClick={() => { setCoachForm({ type: 'coaching', purpose: '', notes: '', scheduled_date: '' }); setCoachOpen(true); }}><Zap className="h-4 w-4 mr-2" />{t('ops.ops.scheduleCoaching')}</Button>
                          <Button variant="outline" className="flex-1" onClick={() => { setCoachForm({ type: 'review', purpose: '', notes: '', scheduled_date: '' }); setCoachOpen(true); }}><BarChart3 className="h-4 w-4 mr-2" />{t('ops.ops.performanceReview')}</Button>
                        </div>
                        <div className="divide-y">
                          {coaching.length === 0 ? <p className="text-sm text-gray-400 py-4 text-center">{t('ops.ops.noCoaching') || 'No coaching or reviews scheduled'}</p> :
                            coaching.map(c => (
                              <div key={c.id} className="py-2 text-sm">
                                <div className="flex justify-between"><span className="font-medium">{c.type === 'review' ? t('ops.ops.performanceReview') : t('ops.ops.scheduleCoaching')}</span><Badge variant="outline">{c.status}</Badge></div>
                                <div className="text-gray-600">{c.purpose} {c.scheduled_date ? `· ${c.scheduled_date}` : ''}</div>
                                {c.notes && <div className="text-gray-500 text-xs mt-1">{c.notes}</div>}
                              </div>
                            ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* Coaching form dialog */}
              <Dialog open={coachOpen} onOpenChange={setCoachOpen}>
                <DialogContent>
                  <DialogHeader><DialogTitle>{coachForm.type === 'review' ? t('ops.ops.performanceReview') : t('ops.ops.scheduleCoaching')}</DialogTitle><DialogDescription>{t('ops.ops.coachingDesc') || 'Schedule a coaching session or performance review for this operator.'}</DialogDescription></DialogHeader>
                  <div className="space-y-3">
                    <Input placeholder={t('ops.ops.coachingPurpose') || 'Purpose'} value={coachForm.purpose} onChange={e => setCoachForm(f => ({ ...f, purpose: e.target.value }))} />
                    <Input type="date" value={coachForm.scheduled_date} onChange={e => setCoachForm(f => ({ ...f, scheduled_date: e.target.value }))} />
                    <textarea className="w-full border rounded p-2 text-sm" rows={3} placeholder={t('ops.ops.coachingNotes') || 'Notes'} value={coachForm.notes} onChange={e => setCoachForm(f => ({ ...f, notes: e.target.value }))} />
                    <Button onClick={submitCoaching} disabled={!coachForm.purpose && !coachForm.scheduled_date} className="w-full">{t('common.save') || 'Save'}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          ) : (
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-12 text-center"><Users className="h-16 w-16 text-gray-400 mx-auto mb-4" /><h3 className="text-lg font-medium text-gray-900 mb-2">{t('ops.ops.selectAnOperator')}</h3><p className="text-gray-500">{t('ops.ops.chooseOperator')}</p></CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageOperators;
